import { PAYMENT_STATUSES } from "@equidefi/shared/constants/payments";
import { latestInvestorPayment } from "@equidefi/shared/helpers/payments";
import type { DTO } from "@equidefi/shared/types/dto";
import { Icon } from "@equidefi/ui/icon";
import type React from "react";

import { VAULT_ROUTES } from "@/constants/routes";

import { InvestmentStepCard } from "./InvestmentStepCard";

export const PaymentStepCard: React.FC<{ investment: DTO.Investment }> = ({ investment }) => {
  const to = VAULT_ROUTES.payments(investment.id);
  const latest = latestInvestorPayment(investment.payments);

  if (investment.is_funded || latest?.status === PAYMENT_STATUSES.SUCCEEDED) {
    return (
      <InvestmentStepCard
        scheme="green"
        label="COMPLETE"
        heading="Payment Successful"
        text="Your payment was received successfully!"
        icon={Icon.DollarSign}
        to={to}
      />
    );
  }

  if (!latest) {
    return (
      <InvestmentStepCard
        scheme="yellow"
        label="ACTION NEEDED"
        heading="Payment Required"
        text="Select a payment method to fund your investment."
        icon={Icon.DollarSign}
        to={to}
      />
    );
  }

  if (latest.status === PAYMENT_STATUSES.REFUNDED) {
    return (
      <InvestmentStepCard
        scheme="red"
        label="REFUNDED"
        heading="Payment Refunded"
        text="Your payment has been refunded by the issuer."
        icon={Icon.DollarSign}
        to={to}
      />
    );
  }

  if (latest.status === PAYMENT_STATUSES.CANCELED) {
    return (
      <InvestmentStepCard
        scheme="red"
        label="CANCELLED"
        heading="Payment Cancelled"
        text="Your payment was cancelled. Select a new payment method to fund your investment."
        icon={Icon.DollarSign}
        to={to}
      />
    );
  }

  if (latest.is_expired || latest.status === PAYMENT_STATUSES.EXPIRED) {
    return (
      <InvestmentStepCard
        scheme="red"
        label="EXPIRED"
        heading="Payment Expired"
        text="Your payment has expired. Retry your payment to fund your investment."
        icon={Icon.DollarSign}
        to={to}
      />
    );
  }

  if (latest.status === PAYMENT_STATUSES.REQUIRES_PAYMENT_METHOD) {
    return (
      <InvestmentStepCard
        scheme="red"
        label="FAILED"
        heading="Payment Failed"
        text="Your payment failed. Select a new payment method to fund your investment."
        icon={Icon.DollarSign}
        to={to}
      />
    );
  }

  if (latest.status === PAYMENT_STATUSES.UNDERPAID) {
    return (
      <InvestmentStepCard
        scheme="red"
        label="UNDERPAID"
        heading="Payment Underpaid"
        text="The amount received is lower than your investment amount."
        icon={Icon.DollarSign}
        to={to}
      />
    );
  }

  if (latest.status === PAYMENT_STATUSES.PROCESSING) {
    return (
      <InvestmentStepCard
        scheme="blue"
        label="PROCESSING"
        heading="Payment Processing"
        text="Your payment is processing."
        icon={Icon.DollarSign}
        to={to}
      />
    );
  }

  if (latest.status === PAYMENT_STATUSES.REQUIRES_CAPTURE) {
    return (
      <InvestmentStepCard
        scheme="blue"
        label="AUTHORIZED"
        heading="Payment Authorized"
        text="Your card will be charged once your investment is approved."
        icon={Icon.DollarSign}
        to={to}
      />
    );
  }

  if (latest.status === PAYMENT_STATUSES.REQUIRES_ACTION) {
    return (
      <InvestmentStepCard
        scheme="yellow"
        label="ACTION NEEDED"
        heading="Payment Requires Action"
        text="Complete the remaining step to finish your payment."
        icon={Icon.DollarSign}
        to={to}
      />
    );
  }

  return (
    <InvestmentStepCard
      scheme="yellow"
      label="PENDING"
      heading="Payment Pending"
      text="Your payment is pending until the funds are received by the issuer."
      icon={Icon.DollarSign}
      to={to}
    />
  );
};

export default PaymentStepCard;
